import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
//MUI
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    Chip,
    Dialog,
    DialogTitle,
    DialogActions,
    Button,
    DialogContent,
    ListItemIcon,
    ListItemText,
    Avatar,
    Stack,
    Typography,
    Autocomplete,
    TextField,
    IconButton,
    Tooltip,
    Icon,
    FormHelperText,
    Paper,
    CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useFormik } from 'formik';
import yup from '@/libs/yup';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { SearchProfileDocument } from '@/graphql/generated/search-profile.generated';
import {
    CloseRounded,
    SearchOffRounded,
    SearchRounded,
    TipsAndUpdatesRounded,
    VerifiedRounded,
} from '@mui/icons-material';
import { ClearIcon } from '@mui/x-date-pickers/icons';
import { enqueueSnackbar } from 'notistack';
import { NetsCompetitorsDocument } from '@/graphql/generated/nets-competitors.generated';
import { AddCompetitorsDocument } from '@/graphql/generated/add-competitors.generated';
import { DashSourceProfilesDocument } from '@/graphql/generated/dash-source-profiles.generated';
import { DashSourceGroup, PlanLimit, TeamPermission } from '@/graphql/generated/types.generated';
import { PlanLimitsDocument } from '@/graphql/generated/plan-limits.generated';
import { debounce } from 'lodash';
import { TotalCompetitorsDocument } from '@/graphql/generated/total-competitors.generated';
import { useParams } from 'react-router-dom';
import { useTeamPermissions } from '@/providers/team-permissions-provider';
import PlanLimitUpgrade from '@/components/billing/PlanLimitUpgrade';

const StyledChip = styled(Chip)({
    '&.MuiChip-root': {
        gap: '6px',
    },
});

export interface AddCompetitorDialogProps {
    open: boolean;
    onClose: (refreshList: boolean) => void;
    // Net or dash source code (e.g. INSTAGRAM, INSTAGRAM_PUBLIC) to preselect when opened from a specific network.
    defaultNetCode?: string;
}

type CompetitorSourceChip = {
    label?: string | null;
    value: string;
    avatar?: string | null;
    color?: string | null;
};

type CompetitorOption = {
    value: string;
    label: string;
    altLabel?: string | null;
    verified?: boolean;
    avatar?: string | null;
    avatarNet: string;
};

export default function AddCompetitorDialog(props: AddCompetitorDialogProps) {
    const { data: netsCompetitors } = useQuery(NetsCompetitorsDocument);
    const { t } = useTranslation();
    const { onClose, open, defaultNetCode } = props;
    const { dashId } = useParams<{ dashId: string }>();
    const { teamPermissions } = useTeamPermissions();

    const [socialNet, setSocialNet] = useState<CompetitorSourceChip>({ value: '' });

    const [inputValue, setInputValue] = useState('');

    const sourcesSelectOptions = useMemo<CompetitorSourceChip[]>(() => {
        return (
            netsCompetitors?.nets.map((sourceItem) => ({
                label: t(sourceItem.name),
                value: sourceItem.id,
                avatar: `/social/${sourceItem.code.toLowerCase()}.svg`,
            })) || []
        );
    }, [netsCompetitors, t]);

    // Every time the dialog opens, select the network it was opened from, falling back to the first one.
    useEffect(() => {
        if (!open) return;
        const nets = netsCompetitors?.nets || [];
        // Competitor dash sources are FACEBOOK_PUBLIC / INSTAGRAM_PUBLIC, while nets are FACEBOOK / INSTAGRAM.
        const netCode = defaultNetCode?.replace(/_PUBLIC$/, '');
        const defaultSource = nets.find((net) => net.code === netCode) || nets[0];
        if (defaultSource) {
            setSocialNet({
                label: t(defaultSource.name),
                value: defaultSource.id,
                avatar: `/social/${defaultSource.code.toLowerCase()}.svg`,
            });
        }
    }, [open, defaultNetCode, netsCompetitors, t]);

    const [addCompetitors] = useMutation(AddCompetitorsDocument, {
        onCompleted: async () => {
            enqueueSnackbar(t('systemsnackbars.competitors.add.success'));
            handleCloseDialog(true);
        },
        onError: (error) => enqueueSnackbar(t(error.message)),
        refetchQueries: [
            {
                query: DashSourceProfilesDocument,
                variables: { dashSourceGroup: DashSourceGroup.Competitors, take: 10, dashId },
            },
            TotalCompetitorsDocument,
            {
                query: PlanLimitsDocument,
                variables: { planLimits: [PlanLimit.CompetitorsProfiles] },
            },
        ],
    });

    const { data: planLimits, loading: planLimitsLoading } = useQuery(PlanLimitsDocument, {
        variables: { planLimits: [PlanLimit.CompetitorsProfiles] },
        onError: (e) => enqueueSnackbar(t(e.message)),
    });

    const { data, loading: totalCompetitorsLoading } = useQuery(TotalCompetitorsDocument);

    const competitorsPlanLimit = planLimits?.planLimits[0];
    const myCompetitors = data?.totalCompetitors;

    const formik = useFormik<{ selectedProfiles: CompetitorOption[] }>({
        initialValues: {
            selectedProfiles: [],
        },
        validationSchema: yup.object({
            selectedProfiles: yup
                .array(
                    yup.object().shape({
                        label: yup.string(),
                        value: yup.string(),
                    }),
                )
                .min(1)
                .max(+(competitorsPlanLimit?.limit || 0) - (myCompetitors || 0)),
        }),
        onSubmit: async (values) => {
            await addCompetitors({
                variables: {
                    competitorProfiles: values.selectedProfiles.map((profile) => profile.value),
                },
            });
        },
    });

    const [searchCompetitor, { data: searchData, loading, error }] = useLazyQuery(SearchProfileDocument, {
        notifyOnNetworkStatusChange: true,
    });

    const handleCloseDialog = (refreshList: boolean = false) => {
        formik.resetForm();
        onClose(refreshList);
    };

    const handleChangeSocialNet = (event: SelectChangeEvent<string>) => {
        const {
            target: { value },
        } = event;
        const sourceSelected = sourcesSelectOptions.find(({ value: valueOption }) => valueOption == value);
        sourceSelected && setSocialNet(sourceSelected);
    };

    const options: CompetitorOption[] = useMemo(() => {
        return (searchData?.searchProfile || []).map((profile) => ({
            value: profile.id,
            label: profile.name,
            avatar: profile.picture,
            avatarNet: `/social/${profile.net?.code.toLowerCase()}.svg`,
            altLabel: profile.username,
            verified: false,
        }));
    }, [searchData?.searchProfile]);

    const fetchOptions = (query: string) => {
        setTimeout(() => {
            searchCompetitor({ variables: { query, netId: socialNet.value } });
        }, 500);
    };

    // Throttled version of the fetch function
    const throttledFetchOptions = useMemo(
        () => debounce(fetchOptions, 300),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [socialNet.value], // Ensures the throttled function is only created once
    );

    const handleInputChange = (value: string) => {
        setInputValue(value);
        if (value) {
            throttledFetchOptions(value);
        }
    };

    const competitorsLimit = +(competitorsPlanLimit?.limit || 0);
    const totalCompetitors = (myCompetitors || 0) + formik.values.selectedProfiles.length;

    /*
     * Both flags must wait for the two queries to resolve. The competitor count and the plan
     * limit load in parallel, so if the count lands first the limit is still 0 — every
     * workspace with at least one competitor would briefly look over its limit, flashing the
     * "not included in your plan" copy and disabling submit until the real limit arrives.
     */
    const limitsLoading = planLimitsLoading || totalCompetitorsLoading;
    const maxCompetitorsReached = !limitsLoading && totalCompetitors > competitorsLimit;
    // The workspace is already full, so nothing can be added — the moment to offer an upgrade.
    const atCompetitorsLimit = !limitsLoading && (myCompetitors || 0) >= competitorsLimit;
    const hasCrudCompetitorsPerm = teamPermissions.includes(TeamPermission.CrudCompetitors);

    if (!hasCrudCompetitorsPerm) {
        return null;
    }

    return (
        <Dialog
            onClose={() => handleCloseDialog()}
            open={open}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                component: 'form',
                onSubmit: formik.handleSubmit,
            }}
        >
            <DialogTitle variant="h5">{t('competitor.add.title')}</DialogTitle>
            <DialogContent
                style={{
                    overflowY: 'visible',
                }}
            >
                <Box display="flex" flex={1} flexDirection={'column'} gap={3}>
                    <FormControl fullWidth>
                        <InputLabel id="select-competitor-source-label">{t('str.social-network')}</InputLabel>
                        <Select
                            size="small"
                            labelId="select-competitor-source-label"
                            disabled={formik.isSubmitting}
                            id="select-competitor-source"
                            value={socialNet?.value || ''}
                            label={t('str.social-network')}
                            onChange={handleChangeSocialNet}
                            renderValue={() =>
                                socialNet && (
                                    <Stack direction="row" gap={2}>
                                        {socialNet.avatar && (
                                            <Avatar
                                                variant="square"
                                                src={socialNet.avatar}
                                                alt={socialNet.label || socialNet.value}
                                                sx={{ width: 20, height: 20 }}
                                            />
                                        )}
                                        <Typography variant="body2">{socialNet.label}</Typography>
                                    </Stack>
                                )
                            }
                        >
                            {sourcesSelectOptions.map((source) => (
                                <MenuItem key={source.value} value={source.value}>
                                    {source.avatar && (
                                        <ListItemIcon>
                                            <Avatar
                                                variant="square"
                                                src={source.avatar}
                                                alt={source.label || source.value}
                                                sx={{ width: 20, height: 20 }}
                                            />
                                        </ListItemIcon>
                                    )}
                                    <ListItemText>{source.label}</ListItemText>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <Autocomplete
                            clearOnBlur={false}
                            multiple
                            disabled={atCompetitorsLimit}
                            disableClearable
                            options={options}
                            filterOptions={(options) => options}
                            inputValue={inputValue}
                            value={formik.values.selectedProfiles}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('competitor.add.input.label')}
                                    slotProps={{
                                        input: {
                                            ...params.InputProps,
                                            type: 'search',
                                            startAdornment: (
                                                <>
                                                    <SearchRounded color="action" />
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            endAdornment: (
                                                <>
                                                    {loading ? (
                                                        <CircularProgress
                                                            color="inherit"
                                                            size={20}
                                                            sx={{ marginRight: -3 }}
                                                        />
                                                    ) : (
                                                        <IconButton
                                                            size="small"
                                                            sx={{ marginRight: -4 }}
                                                            color="inherit"
                                                            onClick={() => {
                                                                setInputValue('');
                                                                formik.setFieldValue('selectedProfiles', []);
                                                            }}
                                                        >
                                                            <CloseRounded />
                                                        </IconButton>
                                                    )}
                                                </>
                                            ),
                                        },
                                    }}
                                />
                            )}
                            onInputChange={(e, inputValue) => handleInputChange(inputValue)}
                            renderOption={(props, option) => {
                                const { key, ...optionProps } = props;
                                return (
                                    <li key={key} {...optionProps}>
                                        <Stack direction="row" alignItems="center" gap={2}>
                                            {option.avatar && (
                                                <Avatar
                                                    src={option.avatar}
                                                    alt={option.label || option.altLabel || option.value}
                                                    sx={{ width: 32, height: 32 }}
                                                />
                                            )}
                                            {option.avatarNet && (
                                                <Avatar
                                                    variant="square"
                                                    src={option.avatarNet}
                                                    alt={option.label || option.altLabel || option.value}
                                                    sx={{ width: 20, height: 20 }}
                                                />
                                            )}
                                            <Typography variant="body1">{option.label}</Typography>
                                            {option.altLabel && (
                                                <Typography variant="body2">{option.altLabel}</Typography>
                                            )}
                                            {option.verified && (
                                                <IconButton color={'primary'} size="small">
                                                    <VerifiedRounded />
                                                </IconButton>
                                            )}
                                        </Stack>
                                    </li>
                                );
                            }}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => {
                                    const { key, ...tagProps } = getTagProps({ index });
                                    return (
                                        <Tooltip title={`${option.label}`} key={key}>
                                            <StyledChip
                                                {...tagProps}
                                                size="small"
                                                variant="filled"
                                                avatar={
                                                    <Avatar
                                                        variant="square"
                                                        src={option.avatarNet}
                                                        alt={option.label || option.altLabel || option.value}
                                                    />
                                                }
                                                label={option.label || option.altLabel}
                                                deleteIcon={<ClearIcon />}
                                            />
                                        </Tooltip>
                                    );
                                })
                            }
                            onChange={(event, newValue) => {
                                formik.setFieldValue('selectedProfiles', newValue);
                            }}
                            slots={{
                                paper: ({ children }) => {
                                    const noOptions = options.length === 0;

                                    return noOptions || error ? (
                                        <Paper
                                            sx={{
                                                padding: (theme) => theme.spacing(1.75, 2),
                                                borderRadius: 2,
                                            }}
                                            elevation={8}
                                        >
                                            <Stack
                                                direction="row"
                                                gap={2}
                                                color="text.disabled"
                                                alignItems="center"
                                            >
                                                <Icon>
                                                    {error ? <TipsAndUpdatesRounded /> : <SearchOffRounded />}
                                                </Icon>
                                                <Typography variant="body1">
                                                    {error
                                                        ? t(error.message)
                                                        : t('search.empty-state.no-results')}
                                                </Typography>
                                            </Stack>
                                        </Paper>
                                    ) : (
                                        <Paper
                                            sx={{
                                                padding: (theme) => theme.spacing(1, 0),
                                                borderRadius: 2,
                                            }}
                                            elevation={8}
                                        >
                                            {children}
                                        </Paper>
                                    );
                                },
                            }}
                        />
                        <FormHelperText>
                            {t('str.number.added.competitors', {
                                current: totalCompetitors,
                                limit: competitorsPlanLimit?.limit,
                            })}
                        </FormHelperText>
                    </FormControl>
                    {(atCompetitorsLimit || maxCompetitorsReached) && (
                        <PlanLimitUpgrade
                            planLimit={PlanLimit.CompetitorsProfiles}
                            onUpgrade={() => handleCloseDialog()}
                            message={
                                // A zero limit means the plan has no competitor tracking at all,
                                // not that the workspace filled it up.
                                competitorsLimit === 0
                                    ? t('dashboard.competitors.unavailable.support-text')
                                    : t('plan-limit.competitors.message', { limit: competitorsLimit })
                            }
                        />
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => handleCloseDialog()} disabled={formik.isSubmitting}>
                    {t('str.action.escape.cancel')}
                </Button>
                <Button
                    type="submit"
                    loading={formik.isSubmitting}
                    disabled={!formik.dirty || maxCompetitorsReached}
                >
                    {t('str.action.add-competitor')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
